



import { mapGetters, mapState } from 'vuex';
import Component from 'vue-class-component';
import { Vue, Watch } from 'vue-property-decorator';
import DateTimeHelper from '@/_helpers/date-time.helper';
import { TEvent, TEventSettings } from '@/_types/event.type';
import { TContact } from '@/_types/contact.type';
import EventLanguageSelector from '@/_components/create-event/event-language-selector.vue';
import { TAbbreviations, TLanguageName } from '@/_modules/events/types/event.type';
import timezone from '@/_modules/events/components/timezones.json';
import { TTimezoneInfo } from '@/_types/timezone-info.type';
import { Validations } from 'vuelidate-property-decorators';
import { TVuelidateRuleSet } from '@/_types/vuelitation-rule-set.type';
import { required } from 'vuelidate/lib/validators';

import iconHome from '@/_modules/icons/components/sidebar/icon-home.vue';
import iconInfo from '@/_modules/icons/components/sidebar/icon-info.vue';
import IconProgram from '@/_modules/icons/components/sidebar/icon-program.vue';
import iconHall from '@/_modules/icons/components/sidebar/icon-hall.vue';
import iconContacts from '@/_modules/icons/components/sidebar/icon-contacts.vue';
import iconMeetings from '@/_modules/icons/components/sidebar/icon-meetings.vue';
import iconDiscussions from '@/_modules/icons/components/sidebar/icon-discussions.vue';
import iconNotes from '@/_modules/icons/components/sidebar/icon-notes.vue';
import iconResult from '@/_modules/icons/components/sidebar/icon-result.vue';
import iconTextChats from '@/_modules/icons/components/sidebar/icon-text-chats.vue';
import iconNews from '@/_modules/icons/components/sidebar/icon-news.vue';

import _cloneDeep from 'lodash.clonedeep';
import VueI18n from 'vue-i18n';
import TranslateResult = VueI18n.TranslateResult;

export const MENU_PROPERTIES_KEY_NAME = 'eventMenuItemProperties';

const isValidEndDateNotInPast = (endDate: Date, date_start: Date): boolean => { // TODO: move this to helper
  if (date_start && endDate) {
    return true;
  }

  if (date_start && endDate) {
    const _startDate: number = new Date(date_start).getTime() / 1000;
    const _dateEnd = new Date(endDate).getTime() / 1000;
    return _dateEnd > _startDate;
  } else {
    return true;
  }
};

type TFormData = {
  title?: string;
  date_start: Date;
  date_end: Date;
  event_type_id?: number;
  languages?: string[];
  time_region: string;
}

type TSideBarMenuItemProperties = {
  isShown: boolean;
}

type TSideBarMenuManagedItem = {
  properties?: TSideBarMenuItemProperties;
  title?: TranslateResult;
  iconComponentName: string;
}

@Component({
  components: {
    EventLanguageSelector,
    iconHome,
    iconInfo,
    IconProgram,
    iconHall,
    iconContacts,
    iconMeetings,
    iconDiscussions,
    iconNotes,
    iconResult,
    iconTextChats,
    iconNews,
  },
  computed: {
    ...mapState('eventStore', ['categoryList', 'typeList']),
    ...mapGetters({
      event: '_eventStore/event',
      eventSettings: '_eventStore/eventSettings',
      myself: 'promoPageStore/contact',
    }),
  }
})
export default class CabinetEventSettings extends Vue {

  @Validations()
  public readonly validations: TVuelidateRuleSet<TFormData> = {
    formData: {
      title: {
        required,
      },
      date_start: {
        required,
      },
      date_end: {
        required,
        isValidEndDate(date_end: Date, formData: TFormData): boolean {
          return isValidEndDateNotInPast(date_end, formData.date_start);
        }
      },
      time_region: {
        required,
      }
    },
  }

  public languages: string[] = [];
  public formData: TFormData = {
    title: '',
    date_start: null,
    date_end: null,
    event_type_id: null,
    time_region: ''
  };

  public endDateMinimum: Date = new Date();
  public isShowStartDate: boolean = false;
  public isShowEndDate: boolean = false;
  public isShowTimezoneLocation: boolean = false;
  public isShowEventType: boolean = false;
  public isMultipleLanguages: boolean = false;
  public isSendSuccess: boolean = false;
  public updatedHintText: TranslateResult = '';
  public timezone: TTimezoneInfo[] = timezone;
  public timezoneLocation: TTimezoneInfo = {} as TTimezoneInfo;
  public allowEventSettingsReRendering: boolean = true;

  public menuItems: {[key: string]: TSideBarMenuManagedItem} = {
    // 'event_info': {
    //   properties: {
    //     isShown: true,
    //   },
    //   title: this.$t('sideBar.info'),
    //   iconComponentName: 'icon-home',
    // },
    promo_live: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.live'),
      iconComponentName: 'icon-info',
    },
    news: {
      properties: {
        isShown: true,
      },
      title: this.$t('sideBar.news'),
      iconComponentName: 'icon-news',
    },
    promo_program: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.program'),
      iconComponentName: 'icon-program',
    },
    promo_page_events_companies: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.hall'),
      iconComponentName: 'icon-hall',
    },
    promo_contacts: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.contacts'),
      iconComponentName: 'icon-contacts',
    },
    text_chats: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.textChats'),
      iconComponentName: 'icon-text-chats',
    },
    notes_list: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.notes'),
      iconComponentName: 'icon-notes',
    },
    promo_page_calendar: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.calendar'),
      iconComponentName: 'icon-meetings',
    },
    result: {
      properties: {
        isShown: true,
      },
      title: this.$t('organizerCabinet.sections.eventSettings.menu.result'),
      iconComponentName: 'icon-result',
    },
  };

  public readonly event: TEvent;
  public readonly myself: TContact;
  public readonly eventSettings: TEventSettings;

  public get eventId(): number {
    return (this.$route.params.eventId && parseInt(this.$route.params.eventId, 10)) || null;
  }

  public get isEventOrganizer(): boolean {
    if (this.event && this.myself && this.myself.user) {
      return this.event.creator_user_id === this.myself.user.id;
    }
    return false;
  }

  public get menuItemKeyNames(): string[] {
    return Object.keys(this.menuItems);
  }

  @Watch('event', { immediate: true, deep: true })
  private onEventInfoChange(): void {
    if(!this.event) { return; }

    this.formData.title = this.event.title;
    this.formData.date_start = new Date(this.event.date_start);
    this.formData.date_end = new Date(this.event.date_end);
    this.formData.event_type_id = this.event.event_type.id;
    this.formData.time_region = this.event.time_region;
    this.languages = this.event.languages;
    this.formData.time_region = this.event.time_region;

    for (let i = 0; i < timezone.length; i++) {
      if(timezone[i].utc.indexOf(this.formData.time_region) > -1) {
        this.timezoneLocation = timezone[i];
      }
    }

  }

  @Watch('eventSettings', {immediate: true})
  private onEventSettingsChange(): void {
    this.initEventMenuItemProperties();
  }

  @Watch('timezoneLocation', {immediate: true})
  private onTimezoneLocationChanged(): void {
    if(this.timezoneLocation && !this.timezoneLocation.utc) { return; }
    this.formData.time_region = this.timezoneLocation.utc[0];
  }

  public created(): void {
    this.updateEventTypes();
  }

  private async updateEventTypes(): Promise<void> {
    await this.$store.dispatch('eventStore/eventType');
  }

  private initEventMenuItemProperties(): void {

    if (!this.allowEventSettingsReRendering) {
      return;
    }

    const defaultProperties: TSideBarMenuItemProperties = {
      isShown: true
    };

    if (this.eventSettings && this.eventSettings.layout && this.eventSettings.layout[MENU_PROPERTIES_KEY_NAME]) {
      const keynamesFromStore: string[] = Object.keys(this.eventSettings.layout[MENU_PROPERTIES_KEY_NAME]);
      for (let i = 0; i < keynamesFromStore.length; i++) {
        const key: string = keynamesFromStore[i];
        if (Object.prototype.hasOwnProperty.call(this.menuItems, key)) {
          this.menuItems[key].properties = _cloneDeep(this.eventSettings.layout[MENU_PROPERTIES_KEY_NAME][key]);
        }
      }
    } else {
      const keynamesLocal = this.menuItemKeyNames;
      for (let i = 0; i < keynamesLocal.length; i++) {
        this.menuItems[keynamesLocal[i]].properties = _cloneDeep(defaultProperties);
      }
    }

    this.menuItems = Object.assign(this.menuItems, {});
  }

  private autoSetEndDate(val: Date): void {

    const start: number = new Date(this.formData.date_start).getTime() / 1000;
    const end: number = new Date(this.formData.date_end).getTime() / 1000;

    // minimum end date is 1 hour ahead, used in the :min-date prop
    this.endDateMinimum = new Date(start + 60 * 60 * 1000);

    // val has to be present and be a JS Date object. Simple check using getTime
    if (val && ('getTime' in val)) {
      if (!this.formData.date_end || start > end) {
        const date_end = new Date(val);
        date_end.setHours(23, 59, 59);

        // If auto-set date_end time is closer than 1 hour, move it to next day 23:59
        if (date_end.getTime() - val.getTime() < (60 * 60 * 1000)) {
          date_end.setTime(date_end.getTime() + 1000 * 60 * 60 * 24);
        }
      }
    }
  }

  private checkEndDate(val: Date): void {
    if (this.formData.date_start === this.formData.date_end) {
      const startDate = new Date(this.formData.date_end);

      // Set hours to date_start.hours + 1
      if (val.getTime() - +startDate < 60 * 60 * 1000) {
        this.formData.date_end = new Date(startDate.getTime() + 60 * 60 * 1000);
      }
    }
  }

  private getCalendarDateTimeFormat(): any { // TODO: add type
    const lang = this.$route.params.lang;
    let textData: TAbbreviations;

    const textStrings: TLanguageName = {
      zh: {
        firstDay: 0,
        dayAbbreviation: ['日', '一', '二', '三', '四', '五', '六'],
        dayList: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
        monthList: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
        monthLongList: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
      },
      vi: {
        firstDay: 1,
        dayAbbreviation: ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'],
        dayList: ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'],
        monthList: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
        monthLongList: ['Tháng Một', 'Tháng Hai', 'Tháng Ba', 'Tháng Tư', 'Tháng Năm', 'Tháng Sáu', 'Tháng Bảy', 'Tháng Tám', 'Tháng Chín', 'Tháng Mười', 'Tháng Mười Một', 'Tháng Mười Hai']
      },
      en: {
        firstDay: 0,
        dayAbbreviation: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
        dayList: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthList: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        monthLongList: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      },
      lt: {
        firstDay: 1,
        dayAbbreviation: ['S.', 'P.', 'A.', 'T.', 'K.', 'Pn.', 'Š.'],
        dayList: ['S.', 'P.', 'A.', 'T.', 'K.', 'Pn.', 'Š.'],
        monthList: ['Saus', 'vas', 'kovas', 'bal', 'geg', 'birz', 'liepa', 'rugp', 'rugs', 'spalis', 'lapkr', 'gr'],
        monthLongList: ['Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis', 'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'],
      },
      ru: {
        firstDay: 1,
        dayAbbreviation: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        dayList: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        monthList: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
        monthLongList: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
      },
      uk: {
        firstDay: 1,
        dayAbbreviation: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        dayList: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        monthList: ['Січ', 'Лют', 'Берез', 'Квіт', 'Трав', 'Черв', 'Лип', 'Серп', 'Верес', 'Жовт', 'Листоп', 'Груд'],
        monthLongList: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
      }
    };

    if (!textStrings[lang]) {
      textData = textStrings.en;
    } else {
      textData = textStrings[lang];
    }

    return {
      formatDisplay(date: Date): string {
        return `${textData.dayList[date.getDay()]}, ${textData.monthList[date.getMonth()]} ${date.getDate()}`;
      },
      formatMonth(date: Date): string {
        return `${textData.monthLongList[date.getMonth()]} ${date.getFullYear()}`;
      },
      getWeekDayArray(): string[] {
        const firstDayOfWeek = textData.firstDay ? textData.firstDay : 0; // firstDay or fallback to Sunday
        const beforeArray: string[] = [];
        const afterArray: string[] = [];
        for (let i = 0; i < textData.dayAbbreviation.length; i++) {
          if (i < firstDayOfWeek) {
            afterArray.push(textData.dayAbbreviation[i]);
          } else {
            beforeArray.push(textData.dayAbbreviation[i]);
          }
        }

        return beforeArray.concat(afterArray);
      },
      getMonthList(): string[] {
        return textData.monthList;
      },
      formatDateDisplay(date: Date): string {
        return `${date.getDate()} ${textData.monthList[date.getMonth()]}`;
      },
    };
  }

  private firstDayOfWeek(): number {
    if (this.$i18n.locale === 'ru' || this.$i18n.locale === 'lt' || this.$i18n.locale === 'uk' || this.$i18n.locale === 'vi') {
      return 1;
    }

    return 0;
  }

  private async patchEvent(): Promise<void> {
    this.$v.formData.$touch();

    if (!this.formData || this.$v.formData.$pending || this.$v.formData.$invalid) {
      return;
    }

    const payload = {
      eventId: this.eventId,
      formData: Object.assign({}, this.formData, {
        date_start: DateTimeHelper.dateToApiDate(this.formData.date_start),
        date_end: DateTimeHelper.dateToApiDate(this.formData.date_end),
      }),
    };

    const result = await this.$store.dispatch('_eventStore/editEvent', payload);
    if (result) {
      this.$v.$reset();
      this.isSendSuccess = true;
      this.allowEventSettingsReRendering = false; // AW-1788 small UX bug #3 from comments
      await this.$store.dispatch('_eventStore/refresh');
      this.allowEventSettingsReRendering = true;
      this.updatedHintText = this.$t('eventPage.edit.updatedSuccess');
    } else {
      this.isSendSuccess = false;
      this.updatedHintText = this.$t('eventPage.edit.updatedError');
    }

    setTimeout(() => {
      this.updatedHintText = '';
    }, 3000);
  }

  // TODO: refactor
  private patchEventSettings(): void {
    const preparedSettings: {[key: string]: TSideBarMenuItemProperties} = {};
    for (let i = 0; i < this.menuItemKeyNames.length; i++) {
      preparedSettings[this.menuItemKeyNames[i]] = this.menuItems[this.menuItemKeyNames[i]].properties;
    }
    let result: {[key: string]: any} = {
      [MENU_PROPERTIES_KEY_NAME]: preparedSettings
    };
    if (this.eventSettings && this.eventSettings.layout) {
      result = { ..._cloneDeep(this.eventSettings.layout), ...result};
    }
    this.$store.dispatch('_eventStore/patchEventSettings', {eventId: this.eventId, layout: result});
  }

}
